import { DataListView, useModal, useToastr } from '@farmshare/ui-components';
import { startCase } from 'lodash';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
} from '@mui/material';
import { useRecoilValue } from 'recoil';
import { userState } from 'state';

import {
  useChangePasswordMutation,
  useUpdateAuthMethodMutation,
} from '../../../lib/graphql';
import { SetPasswordForm } from './set-password-form';
import { ChangePasswordForm } from './change-password-form';

export const AuthenticationSettings = () => {
  const user = useRecoilValue(userState);
  const { save, ask } = useModal();
  const { push } = useToastr();
  const [updateAuthMethod] = useUpdateAuthMethodMutation();
  const [changePassword] = useChangePasswordMutation();

  // @ts-ignore - auth_method exists on user but TypeScript types may not be updated
  const authMethod = user?.auth_method || 'Not set';
  const isOtpUser = authMethod === 'otp';
  const isPasswordUser = authMethod === 'password';

  const handleSetPassword = () => {
    save({
      type: 'save',
      title: 'Set Password for Sign In',
      initialValues: {
        newPassword: '',
        confirmPassword: '',
      },
      body: <SetPasswordForm />,
      onSubmit: async (values) => {
        // Validate passwords match
        if (values.newPassword !== values.confirmPassword) {
          throw new Error('Passwords do not match');
        }

        // Validate password length
        if (values.newPassword.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }

        try {
          const { data } = await updateAuthMethod({
            variables: {
              targetAuthMethod: 'password',
              newPassword: values.newPassword,
            },
          });

          if (data?.updateAuthMethod?.success) {
            push({
              title: 'Success',
              body:
                data.updateAuthMethod.message ||
                'Password set successfully. You can now sign in with your password.',
              bg: 'success',
            });

            // Refresh the page to update the user state
            window.location.reload();
          } else {
            throw new Error('Failed to set password');
          }
        } catch (error: unknown) {
          throw new Error(
            error instanceof Error
              ? error.message
              : 'An error occurred while setting your password',
          );
        }
      },
    });
  };

  const handleChangePassword = () => {
    save({
      type: 'save',
      title: 'Change Password',
      initialValues: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      body: <ChangePasswordForm />,
      onSubmit: async (values) => {
        // Validate passwords match
        if (values.newPassword !== values.confirmPassword) {
          throw new Error('Passwords do not match');
        }

        // Validate password length
        if (values.newPassword.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }

        try {
          const { data } = await changePassword({
            variables: {
              currentPassword: values.currentPassword,
              newPassword: values.newPassword,
            },
          });

          if (data?.changePassword?.success) {
            push({
              title: 'Success',
              body:
                data.changePassword.message || 'Password changed successfully.',
              bg: 'success',
            });
          } else {
            throw new Error('Failed to change password');
          }
        } catch (error: unknown) {
          throw new Error(
            error instanceof Error
              ? error.message
              : 'An error occurred while changing your password',
          );
        }
      },
    });
  };

  const handleSwitchToOTP = () => {
    ask({
      type: 'ask',
      title: 'Switch to One-Time Password?',
      body: 'You will no longer be able to sign in with your password. Instead, you will receive a one-time code each time you log in.',
      onConfirm: async () => {
        try {
          const { data } = await updateAuthMethod({
            variables: {
              targetAuthMethod: 'otp',
            },
          });

          if (data?.updateAuthMethod?.success) {
            push({
              title: 'Success',
              body:
                data.updateAuthMethod.message ||
                'Authentication method updated. You will now receive one-time codes to sign in.',
              bg: 'success',
            });

            // Refresh the page to update the user state
            window.location.reload();
          } else {
            throw new Error('Failed to switch to OTP');
          }
        } catch (error: unknown) {
          push({
            title: 'Error',
            body:
              error instanceof Error
                ? error.message
                : 'An error occurred while updating your authentication method',
            bg: 'error',
          });
        }
      },
    });
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title="Authentication" />
      <CardContent>
        <Stack spacing={2}>
          <DataListView
            data={[
              {
                title: 'Sign-In Method',
                value:
                  authMethod === 'otp'
                    ? 'One-Time Password (OTP)'
                    : startCase(authMethod),
              },
            ]}
          />

          {isOtpUser && (
            <>
              <Alert severity="info">
                You currently sign in by receiving a one-time code. Set a
                password to sign in faster without waiting for a code.
              </Alert>
              <Box>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSetPassword}
                  fullWidth
                >
                  Set Password for Sign In
                </Button>
              </Box>
            </>
          )}

          {isPasswordUser && (
            <>
              <Alert severity="info">
                You currently sign in with a password. You can change your
                password or switch to one-time codes.
              </Alert>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleChangePassword}
                  fullWidth
                >
                  Change Password
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleSwitchToOTP}
                  fullWidth
                >
                  Switch to One-Time Password (OTP)
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
